import type { ComponentType, SVGProps } from 'react';
import {
  DocumentIcon,
  DocumentTextIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  FilmIcon,
} from '@heroicons/react/24/outline';
import { WPMedia } from '../types/wordpress';

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

export type AttachmentFormat = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'video' | 'file';

export interface AttachmentFormatInfo {
  format: AttachmentFormat;
  label: string;
  icon: HeroIcon;
}

const OFFICE_MIME: Record<string, AttachmentFormat> = {
  'application/pdf': 'pdf',
  'application/msword': 'word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
  'application/vnd.ms-word.document.macroenabled.12': 'word',
  'application/vnd.ms-excel': 'excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
  'application/vnd.ms-excel.sheet.macroenabled.12': 'excel',
  'application/vnd.ms-powerpoint': 'powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'powerpoint',
};

const FORMAT_META: Record<AttachmentFormat, { label: string; icon: HeroIcon }> = {
  pdf: { label: 'PDF', icon: DocumentIcon },
  word: { label: 'Word', icon: DocumentTextIcon },
  excel: { label: 'Excel', icon: TableCellsIcon },
  powerpoint: { label: 'PowerPoint', icon: PresentationChartBarIcon },
  video: { label: 'Video', icon: FilmIcon },
  file: { label: 'File', icon: DocumentIcon },
};

function extensionFromUrl(url: string): string {
  return url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() || '';
}

export function getAttachmentFormat(media: WPMedia): AttachmentFormatInfo {
  const mime = (media.mime_type || '').toLowerCase();
  const ext = extensionFromUrl(media.source_url);

  let format: AttachmentFormat = OFFICE_MIME[mime] || 'file';
  if (format === 'file') {
    if (mime === 'application/pdf' || ext === 'pdf') format = 'pdf';
    else if (mime.startsWith('video/') || ['mp4', 'mov', 'webm', 'm4v'].includes(ext)) format = 'video';
    else if (['doc', 'docx'].includes(ext)) format = 'word';
    else if (['xls', 'xlsx', 'csv'].includes(ext)) format = 'excel';
    else if (['ppt', 'pptx'].includes(ext)) format = 'powerpoint';
  }

  const meta = FORMAT_META[format];
  const label =
    format === 'file' && ext && ext.length <= 5 ? ext.toUpperCase() : meta.label;

  return { format, label, icon: meta.icon };
}

export function isPdfAttachment(media: WPMedia): boolean {
  return getAttachmentFormat(media).format === 'pdf';
}

export function filterDocumentAttachments(media: WPMedia[]): WPMedia[] {
  return media.filter((item) => {
    const mime = (item.mime_type || '').toLowerCase();
    const ext = extensionFromUrl(item.source_url);
    if (mime === 'application/pdf' || ext === 'pdf') return true;
    if (mime.startsWith('application/')) return true;
    if (mime.startsWith('text/')) return true;
    if (mime.startsWith('video/')) return true;
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'mp4', 'mov', 'webm'].includes(ext)) {
      return true;
    }
    return false;
  });
}

/**
 * Resolve the current ACF `attachment` field to a media ID.
 * Replaced uploads often remain as `parent` media on the post; only this ID is current.
 */
export function resolveAcfAttachmentId(value: unknown): number | null {
  if (value === false || value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) {
    const id = Number(value.trim());
    return id > 0 ? id : null;
  }
  if (typeof value === 'object') {
    const obj = value as { ID?: unknown; id?: unknown };
    const raw = obj.ID ?? obj.id;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.trunc(raw);
    if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
      const id = Number(raw.trim());
      return id > 0 ? id : null;
    }
  }
  return null;
}

export function attachmentLabel(media: WPMedia): string {
  return getAttachmentFormat(media).label;
}

export function dedupeAttachments(media: WPMedia[]): WPMedia[] {
  const seen = new Set<number>();
  return media.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}
